<template>
  <span class="actuals-help-wrap">
    <b-button
      size="sm"
      variant="success"
      title="About Planned vs. Actuals stats"
      aria-label="Help"
      @click="show_help = true"
    >
      <i class="fa fa-info-circle"></i>
    </b-button>

    <b-modal
      v-model="show_help"
      title="Planned vs. Actuals"
      size="lg"
      ok-only
      scrollable
    >
      <div class="actuals-help-body">
        <h4>What this page shows</h4>
        <p>
          This page compares planned trip times to actual HOS and vehicle times for successful {{ row_label_plural }} in the selected date range.
          <span v-if="source === 'tbn'"> Middle days of multi-day trips with filler segments (default 7am-10pm placeholders) are excluded from accuracy and outlier stats.</span>
        </p>
        <p>
          <b>Deltas</b> are actual minus planned, in minutes. A positive delta means the actual event happened
          <b>after</b> planned; a negative delta means it happened <b>before</b> planned.
        </p>

        <hr/>

        <h4>Summary chips</h4>
        <ul>
          <li><b>{{ count_label_title }}</b> — total Actuals page rows in the date range (same count as the Actuals page).</li>
          <li><b>Successful</b> — {{ row_label_plural }} with calculated actual times (success status on the Actuals page).</li>
          <li><b>Errors</b> — {{ row_label_plural }} that failed actuals processing (error status on the Actuals page). Accuracy stats below only include successful {{ row_label_plural }}.</li>
          <li><b>Outliers</b> — successful {{ row_label_plural }} whose HOS start or finish is at least 120 minutes from planned. These often indicate wrong driver login, overlapping assignments, or bad telematics matching.</li>
        </ul>

        <hr/>

        <h4>At a Glance</h4>
        <p>
          Quick comparison of each milestone: median absolute gap from planned, percent on time
          (within &plusmn;30 minutes of planned), count of {{ row_label_plural }} with both planned and actual times,
          and average signed delta (late vs. early tendency).
        </p>
        <p>Milestones on this page: {{ milestone_list }}.</p>

        <hr/>

        <h4>Milestone Accuracy Details</h4>
        <p>
          Expanded stats for each milestone, including median/mean absolute delta, early vs. late counts,
          percentile distribution, and how many {{ row_label_plural }} fall within 5, 10, 15, 30, 60, or 120 minutes of planned.
        </p>
        <ul>
          <li><b>On time</b> — actual within &plusmn;30 minutes of planned.</li>
          <li><b>Early</b> — actual more than 30 minutes before planned.</li>
          <li><b>Late</b> — actual more than 30 minutes after planned.</li>
        </ul>

        <hr/>

        <h4>Likely Invalid Outliers</h4>
        <p>
          The worst HOS timing mismatches (top 5 shown by default; expand for up to 25).
          {{ planned_hos_note }}
          Booking links open the Actuals page for investigation.
        </p>

        <hr/>

        <h4>Driver rankings</h4>
        <ul>
          <li><b>Most On-Time Drivers</b> — drivers with at least 2 successful {{ row_label_plural }} where both HOS start and finish are within &plusmn;30 minutes of planned. Drivers with 0% on-time are hidden.</li>
          <li><b>Least On-Time Drivers</b> — same criteria, but drivers whose HOS times most often diverge from planned.</li>
        </ul>

        <hr/>

        <h4>Breakdown tables</h4>
        <ul>
          <li><b>Trend by Day</b> — daily count, median start/finish gaps, and outlier count.</li>
          <li><b>By Booking Type</b> — accuracy broken down by {{ booking_type_label }}.</li>
          <li><b>By Vehicle Type</b> — accuracy broken down by assigned vehicle type.</li>
        </ul>
      </div>
    </b-modal>
  </span>
</template>

<script>
export default {
  name: 'actuals_stats_help',

  props: {
    source: {
      type: String,
      default: 'cm',
      validator: value => ['cm', 'tbn', 'fasttrak'].includes(value),
    },
  },

  data() {
    return {
      show_help: false,
    }
  },

  computed: {
    booking_system() {
      const names = { cm: 'Coach Manager', tbn: 'TBN', fasttrak: 'FastTrak' }
      return names[this.source] || 'your booking system'
    },
    row_label_plural() {
      if (this.source === 'tbn')
        return 'bookings'
      if (this.source === 'fasttrak')
        return 'reservations'
      return 'movements'
    },
    count_label_title() {
      if (this.source === 'tbn')
        return 'Bookings'
      if (this.source === 'fasttrak')
        return 'Reservations'
      return 'Movements'
    },
    booking_type_label() {
      const labels = { cm: 'charter vs. contract', tbn: 'charter vs. scheduled service', fasttrak: 'booking type' }
      return labels[this.source] || 'booking type'
    },
    milestone_list() {
      if (this.source === 'tbn') {
        return 'HOS Start, HOS Finish, First Departure (vehicle start), and Last Arrival (vehicle finish)'
      }
      return 'HOS Start, HOS Finish, Spot, Pickup, Arrival, Leave, Back, and Vehicle Finish'
    },
    planned_hos_note() {
      if (this.source === 'tbn') {
        return 'Planned times come from the booking\'s first departure and last arrival; actual HOS times come from driver telematics clock-in/out.'
      }
      return 'Planned times come from scheduled milestone times; actual HOS times come from nearest driver login/logout in telematics.'
    },
  },
}
</script>

<style scoped>
.actuals-help-wrap {
  display: inline-block;
  margin-left: 8px;
}
.actuals-help-body {
  font-size: 15px;
  line-height: 1.5;
  text-align: left;
}
.actuals-help-body h4 {
  font-size: 17px;
  font-weight: bold;
  margin-bottom: 8px;
}
.actuals-help-body p {
  margin-bottom: 10px;
}
.actuals-help-body ul {
  padding-left: 20px;
  margin-bottom: 0;
}
.actuals-help-body li {
  margin-bottom: 6px;
}
</style>
